import { Controller } from "stimulus";

export default class extends Controller {
  static targets = [
    "thumbnail",
    "fileName",
    "uploadArea",
    "fileField",
    "error",
    "manualForm",
    "input",
    "detailsSecret",
  ];

  static values = {
    passportDetails: Object,
    maximumFileSize: Number,
    exceedMaxSizeError: String,
    disableAutoSubmit: Boolean,
  };

  // Khai báo các targets
  declare thumbnailTarget: HTMLDivElement;
  declare fileNameTarget: HTMLSpanElement;
  declare uploadAreaTarget: HTMLDivElement;
  declare fileFieldTarget: HTMLInputElement;
  declare errorTarget: HTMLDivElement;
  declare manualFormTarget: HTMLDivElement;
  declare inputTargets: HTMLInputElement[];
  declare detailsSecretTarget: HTMLInputElement;

  declare hasDetailsSecretTarget: boolean;
  declare hasManualFormTarget: boolean;

  declare passportDetailsValue: any;
  declare maximumFileSizeValue: number;
  declare maxFileSize: number;
  declare exceedMaxSizeErrorValue: string;
  declare disableAutoSubmitValue: boolean;

  private defaultMaxFileSize = 10_000_000 as const; // 10MB
  private defaultErrorMessage =
    "Passport file too large, please upload a file 10MB or smaller.";

  initialize(): void {
    this.maxFileSize = this.maximumFileSizeValue || this.defaultMaxFileSize;
  }

  connect(): void {
    let details = {};
    try {
      details = JSON.parse(this.passportDetailsValue || "{}");
    } catch (e) {
      details = {};
    }

    const hasManualInput = this.inputTargets.some(
      (input) => input.value.trim() !== ""
    );
    const hasAIData = details && Object.keys(details).length > 0;

    if (hasAIData || hasManualInput) {
      this.showManualForm();
    } else {
      this.showUpload();
    }
  }

  handleSelection(): void {
    const file = this.fileFieldTarget.files.item(0);
    if (!file) return;

    if (file.size < this.maxFileSize) {
      this.fileNameTarget.innerText = file.name;
      this.clearErrorMessage();

      // Auto-submitting reloads the page, which clears any other file input
      // on the same page (e.g. a VEVO upload picked moments earlier). When
      // this field shares a page with another file field, skip the
      // preview round-trip and let the OCR run as part of the final submit.
      if (this.disableAutoSubmitValue) {
        this.showThumbnail();
      } else {
        const form = this.element.closest("form");
        if (form) {
          (form as HTMLFormElement).requestSubmit();
        }
      }
    } else {
      this.fileFieldTarget.value = null;
      this.errorTarget.innerText =
        this.exceedMaxSizeErrorValue || this.defaultErrorMessage;
    }
  }

  showManualForm(): void {
    this.uploadAreaTarget.classList.add("hidden");
    this.thumbnailTarget.classList.add("hidden");
    if (this.hasManualFormTarget) {
      this.manualFormTarget.classList.remove("hidden");
    }
  }

  handleDelete(): void {
    this.fileFieldTarget.value = null;
    this.clearInputs();
    this.clearHiddenFields();
    this.clearErrorMessage();
    this.showUpload();
  }

  showThumbnail(): void {
    this.thumbnailTarget.classList.remove("hidden");
    this.uploadAreaTarget.classList.add("hidden");
    if (this.hasManualFormTarget) {
      this.manualFormTarget.classList.add("hidden");
    }
  }

  showUpload(): void {
    this.uploadAreaTarget.classList.remove("hidden");
    this.thumbnailTarget.classList.add("hidden");
    if (this.hasManualFormTarget) {
      this.manualFormTarget.classList.add("hidden");
    }
  }

  clearInputs(): void {
    this.inputTargets.forEach((input) => {
      input.value = "";
    });
  }

  clearHiddenFields(): void {
    if (this.hasDetailsSecretTarget) {
      this.detailsSecretTarget.value = "";
    }

    this.uploadAreaTarget
      .querySelectorAll("[type='hidden']")
      .forEach((hiddenEl: HTMLInputElement) => {
        hiddenEl.value = null;
      });
  }

  clearErrorMessage(): void {
    this.errorTarget.innerText = null;
  }
}
